import React, { useMemo, useEffect, useState } from 'react';
import { Button, Message } from '@alifd/next';
import { Input, Form, Table, Space } from 'antd';
import ButtonWithDialog from '@/components/UI/ButtonWithDialog';
import client from '@/lib/feathers';

const { Item } = Form;

const accountingSubjects = client.service('accountingSubjects');

const EditingButtons = ({
  editingSubject,
  setEditingSubject,
  setBackStageUpdateTrigger,
  subjects,
}) => {
  const handleEditSubmit = async () => {
    try {
      const updateSubject = { ...editingSubject };
      const subjectCodes = subjects.map((subject) => subject.subjectCode);
      // 檢查科目代碼重複
      if (subjectCodes.includes(updateSubject.subjectCode)) {
        Message.error('科目代碼重複');
        return;
      }
      // 非大項科目檢查是否有大項存在
      if (
        updateSubject.subjectCode.slice(2) !== '00' &&
        !subjectCodes.includes(
          updateSubject.subjectCode.slice(0, 2).concat('00')
        )
      ) {
        Message.error('請先新增大項科目');
        return;
      }
      // 禁止大項科目改成自己的小項
      const prevSubjectCode = subjects.find(
        (subject) => subject._id === updateSubject._id
      ).subjectCode;
      if (
        prevSubjectCode.slice(0, 2) === updateSubject.subjectCode.slice(0, 2) &&
        prevSubjectCode.slice(2) === '00' &&
        updateSubject.subjectCode.slice(2) !== '00'
      ) {
        Message.error('不可改成自己的小項');
        return;
      }

      delete updateSubject.children;
      delete updateSubject.key;
      await accountingSubjects.update(updateSubject._id, updateSubject);
      setBackStageUpdateTrigger({});
      setEditingSubject({});
      Message.success('修改成功');
    } catch (error) {
      console.error(error);
      Message.error('修改失敗');
    }
  };

  return (
    <Space>
      <Button
        type="primary"
        onClick={handleEditSubmit}
        style={{ width: '70px' }}
      >
        確認
      </Button>
      <Button onClick={() => setEditingSubject({})} style={{ width: '70px' }}>
        取消
      </Button>
    </Space>
  );
};

const ManipulateButtons = ({
  disabled,
  setEditingSubject,
  setBackStageUpdateTrigger,
  subject,
}) => {
  const [timer, setTimer] = useState(null);

  const removeSubjectInterval = useMemo(() => {
    const interval = {
      id: null,
      startInterval() {
        interval.id = setInterval(() => {
          setTimer((prev) => prev - 1);
        }, 800);
      },
      stopInterval() {
        clearInterval(interval.id);
        setTimer(null);
      },
    };
    return interval;
  }, [setTimer]);

  useEffect(() => {
    if (timer === 0) {
      removeSubjectInterval.stopInterval();
      (async () => {
        try {
          await accountingSubjects.remove(subject._id);
          setBackStageUpdateTrigger({});
          Message.success('刪除成功');
        } catch (error) {
          console.error({ error });
          Message.error('刪除失敗');
        }
      })();
    }
  }, [removeSubjectInterval, setBackStageUpdateTrigger, subject._id, timer]);

  const handleActivate = async () => {
    const updateSubject = { ...subject };
    delete updateSubject.children;
    delete updateSubject.key;
    try {
      await accountingSubjects.update(updateSubject._id, {
        ...updateSubject,
        activated: !updateSubject.activated,
      });
      setBackStageUpdateTrigger({});
      Message.success(`${updateSubject.activated ? '停用' : '啟用'}成功`);
    } catch (error) {
      console.error({ error });
      Message.error(`${updateSubject.activated ? '停用' : '啟用'}失敗`);
    }
  };

  const handleRemoveSubmit = () => {
    let childrenLength = 0;
    if (subject.children) {
      childrenLength = subject.children.length;
    }
    if (subject.used || childrenLength > 0) {
      Message.error('此科目已被使用，不可刪除');
    } else {
      setTimer(3);
      removeSubjectInterval.startInterval();
    }
  };
  const handleRemoveCancel = () => {
    removeSubjectInterval.stopInterval();
  };

  return (
    <Space>
      <Button
        type="normal"
        onClick={() => setEditingSubject(subject)}
        style={{ width: '70px' }}
        disabled={disabled}
      >
        編輯
      </Button>
      <Button
        type="normal"
        onClick={handleActivate}
        style={{ width: '70px' }}
        disabled={disabled}
      >
        {subject.activated ? '停用' : '啟用'}
      </Button>
      <Button
        type="primary"
        warning
        onMouseDown={handleRemoveSubmit}
        onMouseUp={handleRemoveCancel}
        onMouseLeave={handleRemoveCancel}
        style={{ width: '70px' }}
        disabled={disabled}
      >
        {timer || '刪除'}
      </Button>
    </Space>
  );
};

export default function EditSubjects({
  backStageUpdateTrigger,
  setBackStageUpdateTrigger,
}) {
  const [showSubjectEditDialog, setShowSubjectEditDialog] = useState(false);
  const [editingSubject, setEditingSubject] = useState({});
  const [subjects, setSubjects] = useState([]);
  const [nestedSubjects, setNestedSubjects] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await accountingSubjects.find({});
        setSubjects(res.data.sort((a, b) => a.subjectCode - b.subjectCode));
      } catch (error) {
        Message.error('讀取會計科目失敗');
      }
    })();
  }, [backStageUpdateTrigger]);

  useEffect(() => {
    const returnData = [];
    if (subjects.length > 0) {
      subjects.forEach((subject) => {
        if (subject.subjectCode.slice(2, 4) === '00') {
          returnData.push({
            ...subject,
            key: subject.subjectCode,
          });
        } else {
          const parentSubject = returnData.find((item) => {
            if (
              item.subjectCode.slice(0, 2) ===
                subject.subjectCode.slice(0, 2) &&
              item.subjectCode.slice(2, 4) === '00'
            ) {
              return true;
            }
            return false;
          });
          if (!parentSubject.children) {
            parentSubject.children = [];
          }
          parentSubject.children.push({
            ...subject,
            key: subject.subjectCode,
          });
        }
      });
    }
    setNestedSubjects(returnData);
  }, [subjects]);

  const handleNewSubmit = async (value) => {
    const { subjectCode, subjectName } = value;
    if (subjects.find((item) => item.subjectCode === subjectCode)) {
      return Message.error('科目代碼已存在');
    }
    if (
      subjectCode.slice(2) !== '00' &&
      !nestedSubjects.find(
        (subject) => subject.subjectCode.slice(0, 2) === subjectCode.slice(0, 2)
      )
    ) {
      return Message.error('請先新增科目大項');
    }
    try {
      await accountingSubjects.create({
        subjectCode,
        subjectName,
        used: false,
        activated: true,
      });
      Message.success('科目新增成功');
      setBackStageUpdateTrigger({});
    } catch (error) {
      console.error(error);
      Message.error('科目新增失敗');
    }
  };

  const subjectsColumns = [
    {
      title: '科目代碼',
      dataIndex: 'subjectCode',
      key: 'subjectCode',
      width: '25%',
      render: (value, subject) => {
        if (subject._id === editingSubject._id) {
          return (
            <input
              value={editingSubject.subjectCode}
              maxLength={4}
              disabled={subject.children}
              onChange={(event) => {
                event.persist();
                setEditingSubject((prev) => ({
                  ...prev,
                  subjectCode: event.target.value,
                }));
              }}
              style={{ width: '70px' }}
            />
          );
        } else {
          return value;
        }
      },
    },
    {
      title: '科目名稱',
      dataIndex: 'subjectName',
      key: 'subjectName',
      width: '20%',
      render: (value, subject) => {
        if (subject._id === editingSubject._id) {
          return (
            <input
              value={editingSubject.subjectName}
              style={{ width: '100px' }}
              onChange={(event) => {
                event.persist();
                setEditingSubject((prev) => ({
                  ...prev,
                  subjectName: event.target.value,
                }));
              }}
            />
          );
        } else {
          return value;
        }
      },
    },
    {
      title: '啟用',
      dataIndex: 'activated',
      key: 'activated',
      render: (value) => (value ? '啟用' : '停用'),
      width: '10%',
    },
    {
      title: '操作',
      key: 'action',
      render: (_value, subject) => {
        const thisSubjectIsEditing = subject._id === (editingSubject._id || '');
        const disabled =
          Object.keys(editingSubject).length !== 0 && !thisSubjectIsEditing;
        return (
          <Space
            style={{
              width: '100%',
            }}
          >
            {thisSubjectIsEditing ? (
              <EditingButtons
                {...{
                  accountingSubjects,
                  editingSubject,
                  setEditingSubject,
                  setBackStageUpdateTrigger,
                  subjects,
                }}
              />
            ) : (
              <ManipulateButtons
                {...{
                  accountingSubjects,
                  disabled,
                  setEditingSubject,
                  setBackStageUpdateTrigger,
                  subject,
                }}
              />
            )}
          </Space>
        );
      },
      width: '30%',
    },
  ];

  return (
    <>
      <ButtonWithDialog
        buttonTitle="編輯會計科目"
        visible={showSubjectEditDialog}
        setVisible={setShowSubjectEditDialog}
      >
        <Form
          onFinish={handleNewSubmit}
          style={{
            marginTop: '1rem',
            width: '600px',
            display: 'flex',
            alignContent: 'center',
            height: '80px',
            padding: '1rem 0',
          }}
        >
          <Item
            name="subjectCode"
            label="科目代號"
            rules={[{ required: true, message: '請輸入科目代號' }]}
            style={{
              width: '200px',
              margin: '0',
              marginBottom: '0',
              marginRight: '0.5rem',
            }}
          >
            <Input />
          </Item>
          <Item
            name="subjectName"
            label="科目名稱"
            rules={[{ required: true, message: '請輸入科目名稱' }]}
            style={{
              width: '300px',
              margin: '0',
              marginBottom: '0',
              marginRight: '0.5rem',
            }}
          >
            <Input />
          </Item>
          <Item
            style={{
              marginLeft: 'auto',
            }}
          >
            <Button type="primary" htmlType="submit">
              新增
            </Button>
          </Item>
        </Form>
        <Table
          columns={subjectsColumns}
          dataSource={nestedSubjects}
          style={{
            height: '600px',
            width: '750px',
          }}
          scroll={{ y: '520px', x: 'max-content' }}
          pagination={false}
        />
      </ButtonWithDialog>
    </>
  );
}
